// "My devices" (STAGE_5 5.0): where the account is signed in, one session per device,
// with "End" on each - the tablet left in the car, the PC at home - and "Sign out
// everywhere". Ending this device's own session is simply signing out.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, App, Button, Flex, List, Modal, Tag, Typography } from 'antd';

import { api } from '@/api/client';
import { ru } from '@/i18n/ru';
import { formatDateTime } from '@/lib/dates';

import { useAuth } from './authContext';

const s = ru.sessions;
const SESSIONS_KEY = ['me', 'sessions'] as const;

export function SessionsModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { logout } = useAuth();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const sessions = useQuery({
    queryKey: SESSIONS_KEY,
    queryFn: ({ signal }) => api.get('/me/sessions', { signal }),
    enabled: open,
  });

  const end = useMutation({
    mutationFn: (deviceId: string) =>
      api.delete('/me/sessions/{device_id}', { path: { device_id: deviceId } }),
    onSuccess: () => {
      void message.success(s.ended);
      void queryClient.invalidateQueries({ queryKey: SESSIONS_KEY });
    },
    onError: () => {
      void message.error(s.failed);
    },
  });

  const endAll = useMutation({
    mutationFn: () => api.post('/auth/logout-all'),
    onSuccess: () => {
      onClose();
      void logout();
    },
    onError: () => {
      void message.error(s.failed);
    },
  });

  return (
    <Modal
      open={open}
      title={s.title}
      onCancel={onClose}
      destroyOnHidden
      footer={
        <Flex justify="space-between">
          <Button danger loading={endAll.isPending} onClick={() => {
              endAll.mutate();
            }}>
            {s.endAll}
          </Button>
          <Button onClick={onClose}>{ru.common.close}</Button>
        </Flex>
      }
    >
      <Alert type="info" showIcon message={s.hint} style={{ marginBottom: 12 }} />
      {sessions.isError ? (
        <Alert type="error" showIcon message={ru.errors.loadFailed} />
      ) : (
        <List
          loading={sessions.isPending}
          locale={{ emptyText: s.empty }}
          dataSource={sessions.data ?? []}
          rowKey="device_id"
          renderItem={(item) => (
            <List.Item
              actions={[
                <Button
                  key="end"
                  loading={end.isPending && end.variables === item.device_id}
                  onClick={() => {
                    // This device: that is signing out.
                    if (item.current) {
                      onClose();
                      void logout();
                    } else {
                      end.mutate(item.device_id);
                    }
                  }}
                >
                  {s.end}
                </Button>,
              ]}
            >
              <List.Item.Meta
                title={
                  <Flex align="center" gap={8} wrap>
                    <span>{item.device_label ?? s.unknownDevice}</span>
                    {item.current && <Tag color="blue">{s.thisDevice}</Tag>}
                  </Flex>
                }
                description={
                  <Typography.Text type="secondary">
                    {ru.dispatch.join(
                      ...(item.role ? [s.mode(ru.roles[item.role])] : []),
                      s.lastActive(formatDateTime(item.last_active_at)),
                    )}
                  </Typography.Text>
                }
              />
            </List.Item>
          )}
        />
      )}
    </Modal>
  );
}
